<template>
  <b-sidebar
    id="add-product-sidebar"
    :visible="productAddSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @hidden="resetForm"
    @change="(val) => $emit('update:productAddSidebarActive', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          Nuevo Producto
        </h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- Body -->
      <validation-observer
        ref="productForm"
        v-slot="{ handleSubmit }"
      >
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
        >
          <!-- Información General -->
          <h4 class="mb-2">
            <feather-icon
              icon="PackageIcon"
              size="18"
              class="mr-1"
            />
            Información General
          </h4>

          <!-- Code -->
          <validation-provider
            v-slot="{ errors }"
            name="Código"
            rules="required|max:50"
          >
            <b-form-group
              label="Código"
              label-for="code"
            >
              <b-form-input
                id="code"
                v-model="productData.code"
                :state="errors.length === 0"
                placeholder="Ej: 100750"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <!-- SKU -->
          <validation-provider
            v-slot="{ errors }"
            name="SKU"
            rules="max:100"
          >
            <b-form-group
              label="SKU"
              label-for="sku"
            >
              <b-form-input
                id="sku"
                v-model="productData.sku"
                :state="errors.length === 0"
                placeholder="Ej: 100750-BLUE"
              />
              <small class="text-muted">Código único del producto + color (opcional)</small>
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <!-- Description -->
          <validation-provider
            v-slot="{ errors }"
            name="Descripción"
            rules="required|max:200"
          >
            <b-form-group
              label="Descripción"
              label-for="description"
            >
              <b-form-textarea
                id="description"
                v-model="productData.description"
                :state="errors.length === 0"
                placeholder="Descripción del producto"
                rows="3"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <!-- Base Price -->
          <validation-provider
            v-slot="{ errors }"
            name="Precio Base"
            rules="decimal:2"
          >
            <b-form-group
              label="Precio Base"
              label-for="base_price"
            >
              <b-form-input
                id="base_price"
                v-model="productData.base_price"
                type="number"
                step="0.01"
                min="0"
                :state="errors.length === 0"
                placeholder="0.00"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <!-- Status -->
          <validation-provider
            v-slot="{ errors }"
            name="Estado"
            rules="required"
          >
            <b-form-group
              label="Estado"
              label-for="is_active"
            >
              <v-select
                id="is_active"
                v-model="productData.is_active"
                :options="statusOptions"
                :reduce="status => status.value"
                :state="errors.length === 0"
                placeholder="Seleccionar estado"
                label="label"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <!-- Propiedades -->
          <h4 class="mb-2 mt-2">
            <feather-icon
              icon="GridIcon"
              size="18"
              class="mr-1"
            />
            Propiedades
          </h4>

          <!-- Color -->
          <validation-provider
            v-slot="{ errors }"
            name="Color"
            rules="required"
          >
            <b-form-group
              label="Color"
              label-for="color"
            >
              <v-select
                id="color"
                v-model="productData.color_id"
                :options="colorOptions"
                :reduce="color => color.id"
                :state="errors.length === 0"
                placeholder="Seleccionar color"
                label="name"
              >
                <template #option="{ name, hex_code }">
                  <div class="d-flex align-items-center">
                    <div
                      v-if="hex_code"
                      class="color-preview mr-2"
                      :style="{ backgroundColor: hex_code }"
                    />
                    <span>{{ name }}</span>
                  </div>
                </template>
              </v-select>
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <!-- Category -->
          <validation-provider
            v-slot="{ errors }"
            name="Categoría"
            rules="required"
          >
            <b-form-group
              label="Categoría"
              label-for="category"
            >
              <v-select
                id="category"
                v-model="productData.category_id"
                :options="categoryOptions"
                :reduce="category => category.id"
                :state="errors.length === 0"
                placeholder="Seleccionar categoría"
                label="name"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <!-- Width -->
          <validation-provider
            v-slot="{ errors }"
            name="Ancho"
            rules="max:50"
          >
            <b-form-group
              label="Ancho"
              label-for="width"
            >
              <b-form-input
                id="width"
                v-model="productData.width"
                :state="errors.length === 0"
                placeholder="Ej: 40mm, 4cm"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <!-- Web Name -->
          <validation-provider
            v-slot="{ errors }"
            name="Nombre Web"
            rules="max:200"
          >
            <b-form-group
              label="Nombre Web"
              label-for="web_name"
            >
              <b-form-input
                id="web_name"
                v-model="productData.web_name"
                :state="errors.length === 0"
                placeholder="Nombre para mostrar en la web"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <!-- Web Description -->
          <validation-provider
            v-slot="{ errors }"
            name="Descripción Web"
          >
            <b-form-group
              label="Descripción Web"
              label-for="web_description"
            >
              <b-form-textarea
                id="web_description"
                v-model="productData.web_description"
                :state="errors.length === 0"
                placeholder="Descripción detallada para la web"
                rows="4"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <!-- Bullet Points -->
          <validation-provider
            v-slot="{ errors }"
            name="Puntos Clave"
          >
            <b-form-group
              label="Puntos Clave"
              label-for="bullet_points"
            >
              <b-form-textarea
                id="bullet_points"
                v-model="productData.bullet_points"
                :state="errors.length === 0"
                placeholder="Características principales del producto (una por línea)"
                rows="4"
              />
              <small class="text-muted">Ingresa una característica por línea</small>
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              variant="primary"
              type="submit"
              block
              :disabled="isSubmitting"
            >
              <b-spinner
                v-if="isSubmitting"
                small
                class="mr-1"
              />
              {{ isSubmitting ? 'Guardando...' : 'Guardar Producto' }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'

export default {
  components: {
    vSelect,
    ValidationProvider,
    ValidationObserver,
  },

  props: {
    productAddSidebarActive: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      isSubmitting: false,
      productData: {
        code: '',
        sku: '',
        description: '',
        base_price: null,
        width: '',
        web_name: '',
        web_description: '',
        bullet_points: '',
        color_id: null,
        category_id: null,
        is_active: 'Y',
      },
      colorOptions: [],
      categoryOptions: [],
      statusOptions: [
        { label: 'Activo', value: 'Y' },
        { label: 'Inactivo', value: 'N' },
      ],
    }
  },

  watch: {
    productAddSidebarActive(val) {
      if (val) {
        this.loadOptions()
      }
    },
  },

  methods: {
    async loadOptions() {
      try {
        // Cargar colores
        const colorsResponse = await axios.get('color?perPage=100&filterField=status&filterValue=Y')
        this.colorOptions = colorsResponse.data.data

        // Cargar categorías
        const categoriesResponse = await axios.get('category?perPage=100&filterField=status&filterValue=Y')
        this.categoryOptions = categoriesResponse.data.data
      } catch (error) {
        this.showErrors(error)
      }
    },

    async onSubmit() {
      this.isSubmitting = true

      try {
        await axios.post('product', this.productData)

        this.$bvToast.toast('Producto creado exitosamente', {
          title: 'Éxito',
          variant: 'success',
          solid: true,
        })

        this.$emit('refreshData')
        this.$emit('update:productAddSidebarActive', false)
      } catch (error) {
        this.showErrors(error)
      } finally {
        this.isSubmitting = false
      }
    },

    resetForm() {
      this.productData = {
        code: '',
        sku: '',
        description: '',
        base_price: null,
        width: '',
        web_name: '',
        web_description: '',
        bullet_points: '',
        color_id: null,
        category_id: null,
        is_active: 'Y',
      }
      this.$refs.productForm.reset()
    },

    showErrors(error) {
      if (error.response && error.response.data && error.response.data.message) {
        this.$bvToast.toast(error.response.data.message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } else {
        this.$bvToast.toast('Ha ocurrido un error inesperado', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.color-preview {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid #ddd;
}

.content-sidebar-header {
  border-bottom: 1px solid #d8d6de;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
